import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { api, fetchWithAuth, ProjectTask, TaskAssignee, User, getFinancialSources, type FinancialSource, Tag } from "@/lib/api";
import { firmAttachmentVisibility } from "@/lib/attachmentVisibility";
import { TagPicker, TagChip } from "@/components/custom/TagPicker";
import { LikeButton } from "@/components/custom/LikeButton";
import { PortalVisibilityPicker } from "@/components/custom/PortalVisibilityPicker";
import { AddFinancialSourceDialog } from "@/components/custom/AddFinancialSourceDialog";
import { TaskAutomationsModal } from "@/components/custom/TaskAutomationsModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  Loader2, ChevronDown, ChevronRight, Paperclip, Send, Check,
  Plus, Pencil, Trash2, UserPlus, Link2, X, GripVertical, ExternalLink, Zap, Eye, Download,
} from "lucide-react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CompleteConfirmDialog } from "@/components/custom/CompleteConfirmDialog";
import { buildTaskCompleteWarning, buildSubTaskCompleteWarning } from "@/lib/taskCompleteWarning";
import { DeleteConfirmDialog } from "@/components/custom/DeleteConfirmDialog";
import { buildTaskDeleteWarning, buildSubTaskDeleteWarning } from "@/lib/taskDeleteWarning";
import { triggerDownload } from "@/lib/download";

function fmt(dateStr: string | null | undefined) {
  if (!dateStr) return null;
  try { return format(parseISO(dateStr), "MMM d, yyyy"); } catch { return dateStr; }
}

function triggerConfetti(x: number, y: number) {
  const container = document.createElement("div");
  container.style.cssText =
    "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;";
  document.body.appendChild(container);

  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#f97316"];
  const count = 14;

  for (let i = 0; i < count; i++) {
    const dot = document.createElement("div");
    const color = colors[i % colors.length];
    const angle = (i / count) * 360 + Math.random() * 20;
    const dist = 35 + Math.random() * 55;
    const dx = Math.cos((angle * Math.PI) / 180) * dist;
    const dy = Math.sin((angle * Math.PI) / 180) * dist - 20; // slight upward bias
    const size = 5 + Math.random() * 5;
    const isRect = Math.random() > 0.5;

    dot.style.cssText = `
      position:absolute;
      left:${x}px;top:${y}px;
      width:${size}px;height:${isRect ? size * 0.5 : size}px;
      background:${color};
      border-radius:${isRect ? "2px" : "50%"};
      transform:translate(-50%,-50%) rotate(${Math.random() * 360}deg);
      opacity:1;
      transition:transform 0.65s cubic-bezier(.2,.8,.4,1), opacity 0.65s ease-out;
    `;
    container.appendChild(dot);

    // Double rAF to ensure transition triggers
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dot.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${Math.random() * 720}deg)`;
        dot.style.opacity = "0";
      });
    });
  }

  setTimeout(() => document.body.removeChild(container), 750);
}

export function taskInitials(name: string) {
  return name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500",
  "bg-amber-500", "bg-rose-500", "bg-cyan-500",
];
function avatarColor(userId: string) {
  let hash = 0;
  for (const c of userId) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ── Assignee popover (FC-style searchable multi-select) ──────────────────────
function AssigneePopover({
  taskId,
  allUsers,
  initialAssignees,
  onChanged,
}: {
  taskId: string;
  allUsers: User[];
  initialAssignees?: TaskAssignee[];
  onChanged?: (assignees: TaskAssignee[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [assignees, setAssignees] = useState<TaskAssignee[]>(initialAssignees ?? []);
  const [loading, setLoading] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(!!(initialAssignees?.length));
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const load = async () => {
    if (loading || bootstrapped) return;
    setLoading(true);
    try {
      const data = await api.getTaskAssignees(taskId);
      setAssignees(data);
      onChanged?.(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setBootstrapped(true);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) load();
  };

  const isAssigned = (userId: string) => assignees.some(a => a.user_id === userId);

  const toggle = async (userId: string) => {
    if (toggling[userId]) return;
    setToggling(prev => ({ ...prev, [userId]: true }));
    try {
      if (isAssigned(userId)) {
        await api.removeTaskAssignee(taskId, userId);
        const next = assignees.filter(a => a.user_id !== userId);
        setAssignees(next);
        onChanged?.(next);
      } else {
        await api.addTaskAssignee(taskId, userId);
        const next = [...assignees, { id: "", task_id: taskId, user_id: userId, list_index: assignees.length, users: allUsers.find(u => u.id === userId) ?? null } as TaskAssignee];
        setAssignees(next);
        onChanged?.(next);
      }
    } catch {
      toast({ title: "Failed to update assignee", variant: "destructive" });
    } finally {
      setToggling(prev => ({ ...prev, [userId]: false }));
    }
  };

  const avatarList = assignees.slice(0, 4);
  const overflow = assignees.length - 4;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-0.5 shrink-0 hover:opacity-80 transition-opacity"
          title="Assign / manage assignees"
          onClick={e => e.stopPropagation()}
        >
          {loading && assignees.length === 0 ? (
            <span className="w-5 h-5 flex items-center justify-center">
              <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
            </span>
          ) : assignees.length === 0 ? (
            /* Always-visible unassigned button */
            <span className="flex items-center justify-center w-6 h-6 rounded border border-blue-400 text-blue-600 hover:bg-blue-50 transition-colors">
              <UserPlus className="w-3.5 h-3.5" />
            </span>
          ) : (
            /* Avatars for assigned users */
            <>
              {avatarList.map(a => {
                const name = a.users?.name ?? allUsers.find(u => u.id === a.user_id)?.name ?? "?";
                return (
                  <span
                    key={a.user_id}
                    className={`w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center -ml-0.5 first:ml-0 ring-1 ring-background ${avatarColor(a.user_id)}`}
                    title={name}
                  >
                    {taskInitials(name)}
                  </span>
                );
              })}
              {overflow > 0 && (
                <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground text-[9px] font-bold flex items-center justify-center -ml-0.5 ring-1 ring-background">
                  +{overflow}
                </span>
              )}
              <span className="w-4 h-4 rounded-full bg-muted/50 text-muted-foreground flex items-center justify-center -ml-0.5 ring-1 ring-background">
                <UserPlus className="w-2.5 h-2.5" />
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="end" onClick={e => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder="Search by name" className="h-8 text-xs" />
          <CommandList className="max-h-52">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty className="text-xs py-3 text-center text-muted-foreground">No users found</CommandEmpty>
                <CommandGroup>
                  {allUsers.map(u => (
                    <CommandItem
                      key={u.id}
                      value={u.name || u.email}
                      onSelect={() => toggle(u.id)}
                      className="text-xs gap-2 cursor-pointer"
                    >
                      <span className={`w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0 ${avatarColor(u.id)}`}>
                        {taskInitials(u.name || u.email)}
                      </span>
                      <span className="flex-1 truncate">{u.name || u.email}</span>
                      {toggling[u.id]
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : isAssigned(u.id) && <Check className="w-3 h-3 text-primary" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Sub-task row with inline tags ────────────────────────────────────────────
function SubTaskRow({
  sub,
  subDone,
  toggling,
  onToggle,
  onUpdate,
  onDelete,
  accountId,
}: {
  sub: ProjectTask;
  subDone: boolean;
  toggling: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onUpdate?: (updated: ProjectTask) => void;
  onDelete?: (subId: string) => void;
  accountId?: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(sub.title);
  const [showSubSourcePicker, setShowSubSourcePicker] = useState(false);
  const [subLocalSourceId, setSubLocalSourceId] = useState<string | null>(sub.financial_source_id ?? null);
  const [subLocalSource, setSubLocalSource] = useState<{ institution_name: string; account_nickname: string | null } | null>(sub.financial_source ?? null);
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);
  const [localAssignee, setLocalAssignee] = useState<{ id: string; name: string; email: string } | null>(
    sub.users ? { id: (sub.users as any).id ?? "", name: (sub.users as any).name ?? "", email: (sub.users as any).email ?? "" } : null
  );

  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    staleTime: 10 * 60 * 1000,
  });

  // Assign through the canonical assignee endpoints, not PUT /api/project-tasks/:id.
  // The PUT writes only project_tasks.assigned_user_id, and the Projects table plus the
  // project task drawer read task_assignees — which is why zero subtask junction rows existed
  // firm-wide and subtask assignees were invisible outside this row. POST writes the junction
  // row and back-fills the column; DELETE removes it and re-points the column.
  //
  // The DELETE must come FIRST when picking someone new: POST only back-fills
  // assigned_user_id when it is null, so posting over an existing assignee would leave the
  // column naming the old user while the junction row names the new one — exactly the
  // disagreement state we refuse to auto-repair elsewhere. DELETE also re-points the column
  // unconditionally, so it still clears correctly on a subtask that has no junction row yet.
  const assignSubTaskMutation = useMutation({
    mutationFn: async (userId: string | null) => {
      const current = localAssignee?.id ?? sub.assigned_user_id ?? null;
      if (current) await api.removeTaskAssignee(sub.id, current);
      if (userId) await api.addTaskAssignee(sub.id, userId);
      return userId;
    },
    onSuccess: (userId) => {
      const matched = userId ? allUsers.find(u => u.id === userId) ?? null : null;
      setLocalAssignee(matched ? { id: matched.id, name: matched.name, email: matched.email } : null);
      setShowAssigneePicker(false);
      onUpdate?.({ ...sub, assigned_user_id: userId } as ProjectTask);
    },
    onError: () => toast({ title: "Failed to update assignee", variant: "destructive" }),
  });

  const { data: subTags = [] } = useQuery<Tag[]>({
    queryKey: ["task-tags", sub.id],
    queryFn: () => api.getTaskTags(sub.id),
  });

  const { data: subAccountSources = [] } = useQuery<FinancialSource[]>({
    queryKey: ["financial-sources", accountId],
    queryFn: () => getFinancialSources(accountId!),
    enabled: !!accountId && showSubSourcePicker,
    staleTime: 60_000,
  });

  const linkSubSourceMutation = useMutation({
    mutationFn: (sourceId: string | null) =>
      fetchWithAuth<any>(`/api/project-tasks/${sub.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ financial_source_id: sourceId }),
      }),
    onSuccess: (_, sourceId) => {
      const matched = sourceId ? subAccountSources.find((s) => s.id === sourceId) : null;
      setSubLocalSourceId(sourceId);
      setSubLocalSource(matched ? { institution_name: matched.institution_name, account_nickname: matched.account_nickname } : null);
      setShowSubSourcePicker(false);
    },
    onError: () => toast({ title: "Failed to update source link", variant: "destructive" }),
  });

  const addTagMutation = useMutation({
    mutationFn: (tagId: string) => api.addTaskTag(sub.id, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["task-tags", sub.id] }),
  });
  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) => api.removeTaskTag(sub.id, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["task-tags", sub.id] }),
  });
  const renameMutation = useMutation({
    mutationFn: (title: string) => api.updateProjectTask(sub.id, { title }),
    onSuccess: (updated) => {
      setIsEditing(false);
      onUpdate?.(updated);
    },
    onError: () => toast({ title: "Failed to rename sub-task", variant: "destructive" }),
  });

  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const commitEdit = () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === sub.title) { setIsEditing(false); setEditTitle(sub.title); return; }
    renameMutation.mutate(trimmed);
  };

  const runDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await api.deleteProjectTask(sub.id);
      onDelete?.(sub.id);
    } catch {
      toast({ title: "Failed to delete sub-task", variant: "destructive" });
      setDeleting(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5 py-0.5">
        <Input
          autoFocus
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
            if (e.key === "Escape") { setIsEditing(false); setEditTitle(sub.title); }
          }}
          onBlur={commitEdit}
          className="h-6 text-xs flex-1"
        />
        {renameMutation.isPending && <Loader2 className="w-3 h-3 animate-spin shrink-0 text-muted-foreground" />}
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2 py-0.5">
      <button
        type="button"
        onClick={onToggle}
        disabled={toggling}
        className={`shrink-0 w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-colors ${subDone ? "bg-green-500 border-green-500" : "border-gray-300 hover:border-green-400"}`}
      >
        {toggling
          ? <Loader2 className="w-2 h-2 animate-spin text-white" />
          : subDone && <Check className="w-2 h-2 text-white" />}
      </button>
      {/* Title + pencil + link grouped together */}
      <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
        <span className={`text-xs min-w-0 line-clamp-2 ${subDone ? "line-through text-muted-foreground" : ""}`}>
          {sub.title}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEditTitle(sub.title); setIsEditing(true); }}
          className="shrink-0 p-0.5 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors"
          title="Rename"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
          disabled={deleting}
          className="shrink-0 p-0.5 rounded hover:bg-red-50 text-rose-500 hover:text-rose-700 transition-colors"
          title="Delete sub-task"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
        <DeleteConfirmDialog
          open={confirmDelete}
          onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}
          subject="sub-task"
          name={sub.title}
          lines={buildSubTaskDeleteWarning()}
          busy={deleting}
          onConfirm={runDelete}
        />
        {accountId && (
          <div className="shrink-0 flex items-center">
            {subLocalSourceId && subLocalSource ? (
              <div className="flex items-center gap-0.5 text-[10px] text-primary bg-primary/10 rounded px-1.5 py-0.5">
                <Link2 className="w-2.5 h-2.5 shrink-0" />
                <span className="max-w-[70px] truncate">{subLocalSource.institution_name}</span>
                <button onClick={() => linkSubSourceMutation.mutate(null)} className="hover:text-destructive ml-0.5" title="Unlink source">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ) : (
              <Popover open={showSubSourcePicker} onOpenChange={setShowSubSourcePicker}>
                <PopoverTrigger asChild>
                  <button className="p-0.5 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-800 transition-colors" title="Link a financial source">
                    <Link2 className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-2 w-56" align="start">
                  <p className="text-xs font-medium mb-1.5 text-muted-foreground">Link Source</p>
                  <Select onValueChange={(v) => linkSubSourceMutation.mutate(v === "none" ? null : v)}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Pick a source…" />
                    </SelectTrigger>
                    <SelectContent>
                      {subAccountSources.length === 0 ? (
                        <SelectItem value="none" disabled>No sources found</SelectItem>
                      ) : (
                        subAccountSources.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.institution_name}{s.account_nickname ? ` — ${s.account_nickname}` : ""}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>
      {subTags.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap shrink-0">
          {subTags.map((tag: Tag) => (
            <TagChip key={tag.id} tag={tag} />
          ))}
        </div>
      )}
      <div className="shrink-0">
        <TagPicker
          appliedTags={subTags}
          onAdd={(tagId) => addTagMutation.mutate(tagId)}
          onRemove={(tagId) => removeTagMutation.mutate(tagId)}
          align="end"
        />
      </div>
      {/* Assignee picker */}
      <Popover open={showAssigneePicker} onOpenChange={setShowAssigneePicker}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="shrink-0 flex items-center justify-center"
            title={localAssignee ? `Assigned to ${localAssignee.name} — click to change` : "Assign to someone"}
            onClick={(e) => e.stopPropagation()}
          >
            {localAssignee ? (
              <span className={`w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${avatarColor(localAssignee.id)}`}>
                {taskInitials(localAssignee.name || localAssignee.email)}
              </span>
            ) : (
              <span className="w-4 h-4 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-primary hover:text-primary transition-colors">
                <UserPlus className="w-2.5 h-2.5" />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end" onClick={(e) => e.stopPropagation()}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-2 py-1">Assign subtask</p>
          {localAssignee && (
            <button
              type="button"
              className="w-full text-left px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded flex items-center gap-1.5"
              onClick={() => assignSubTaskMutation.mutate(null)}
            >
              <X className="w-3 h-3" /> Unassign
            </button>
          )}
          {allUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`w-full text-left px-2 py-1 text-xs rounded flex items-center gap-1.5 ${localAssignee?.id === u.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`}
              onClick={() => assignSubTaskMutation.mutate(u.id)}
            >
              <span className={`w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0 ${avatarColor(u.id)}`}>
                {taskInitials(u.name || u.email)}
              </span>
              <span className="truncate">{u.name || u.email}</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Send to Client button ─────────────────────────────────────────────────────
function SendToClientButton({ task, onSent }: { task: ProjectTask; onSent?: (updated: Partial<ProjectTask>) => void }) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [localSentAt, setLocalSentAt] = useState<string | null>(task.sent_at ?? null);

  const handleSend = async () => {
    setSending(true);
    try {
      const updated = await api.sendTaskToClient(task.id);
      setLocalSentAt(updated.sent_at ?? new Date().toISOString());
      onSent?.(updated);
      toast({ title: localSentAt ? "Task resent to client" : "Task sent to client" });
    } catch {
      toast({ title: "Failed to send task to client", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant={localSentAt ? "outline" : "default"}
        className="h-6 text-xs px-2.5"
        onClick={handleSend}
        disabled={sending}
      >
        {sending
          ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
          : <Send className="w-3 h-3 mr-1" />}
        {localSentAt ? "Resend to Client" : "Send to Client"}
      </Button>
      {localSentAt && (
        <span className="text-[10px] text-muted-foreground">
          Sent {fmt(localSentAt)}
        </span>
      )}
    </div>
  );
}

// ── Main TaskDetailRow ───────────────────────────────────────────────────────
export function TaskDetailRow({
  task,
  onStatusChange,
  onDelete,
  onUpdate,
  accountId,
  autoExpand,
  portalResponseCount,
  sequentialBlockedOn,
}: {
  task: ProjectTask;
  onStatusChange?: (id: string, done: boolean) => void;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<ProjectTask>) => void;
  accountId?: string;
  autoExpand?: boolean;
  portalResponseCount?: number;
  /** Title of the earlier incomplete task this one is waiting on (sequential
   *  section, computed by the parent). Advisory only — the checkbox still works. */
  sequentialBlockedOn?: string | null;
}) {
  const [expanded, setExpanded] = useState(!!autoExpand);
  const rowRef = useRef<HTMLDivElement>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");
  const [newComment, setNewComment] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<User[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [pendingAttachments, setPendingAttachments] = useState<{ id: string; name: string; uploading: boolean }[]>([]);
  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [subTaskToggling, setSubTaskToggling] = useState<Record<string, boolean>>({});
  const [localSubTasks, setLocalSubTasks] = useState<ProjectTask[]>(task.sub_tasks ?? []);
  const [dragSubId, setDragSubId] = useState<string | null>(null);
  const [dragOverSubId, setDragOverSubId] = useState<string | null>(null);
  const [showAddSubTask, setShowAddSubTask] = useState(false);
  const [newSubTaskTitle, setNewSubTaskTitle] = useState("");
  const [addingSubTask, setAddingSubTask] = useState(false);
  const [newSubSourceId, setNewSubSourceId] = useState<string | null>(null);
  const [newSubSourceLabel, setNewSubSourceLabel] = useState<string | null>(null);
  const [showNewSubSourcePicker, setShowNewSubSourcePicker] = useState(false);
  // which picker triggered the add-source modal: "task" | "subtask" | null
  const [addSourceFor, setAddSourceFor] = useState<"task" | "subtask" | null>(null);
  const [editingSource, setEditingSource] = useState<import("@/lib/api").FinancialSource | null>(null);
  // Assignees shown on row (updated when popover reports changes)
  const [rowAssignees, setRowAssignees] = useState<TaskAssignee[]>(task.task_assignees ?? []);
  // Edit state
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDueDate, setEditDueDate] = useState(task.due_date ?? "");
  const [editInternalDue, setEditInternalDue] = useState(task.internal_due_date ?? "");
  const [allowClientUpload, setAllowClientUpload] = useState(task.allow_client_upload);
  const [visibleToEmails, setVisibleToEmails] = useState<string[] | null>(task.visible_to_emails ?? null);
  // ENG-60: can the client actually see files staff attach to this task?
  const firmVisibility = firmAttachmentVisibility({ is_client_task: task.is_client_task, sent_at: task.sent_at });
  const firmVisibilityBadge =
    firmVisibility === "portal-visible" ? (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 font-medium shrink-0">
        Visible to client in portal{visibleToEmails && visibleToEmails.length > 0 ? " (restricted)" : ""}
      </span>
    ) : firmVisibility === "unsent-client-task" ? (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700 font-medium shrink-0">
        Client can't see this yet — task not sent
      </span>
    ) : (
      <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted text-muted-foreground font-medium shrink-0">
        Internal task — not visible to client
      </span>
    );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Completing a task or sub-task cascades (parent → children, last child → parent)
  // and neither cascade is undone by unchecking, so both checkboxes confirm first.
  const [confirmComplete, setConfirmComplete] = useState<
    | { kind: "task"; title: string; x: number; y: number; lines: string[] }
    | { kind: "sub"; sub: ProjectTask; title: string; x: number; y: number; lines: string[] }
    | null
  >(null);
  // ENG-73: per-attachment delete — one row at a time asks to confirm, one at a time is in flight.
  const [confirmDeleteAttachmentId, setConfirmDeleteAttachmentId] = useState<string | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [taskRulesOpen, setTaskRulesOpen] = useState(false);
  const [taskRuleCount, setTaskRuleCount] = useState<number | null>(null);
  const [localSourceId, setLocalSourceId] = useState<string | null>(task.financial_source_id ?? null);
  const [localSource, setLocalSource] = useState<{ institution_name: string; account_nickname: string | null } | null>(task.financial_source ?? null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const done = task.status === "completed" || task.status === "done";

  const { data: accountSources = [] } = useQuery<FinancialSource[]>({
    queryKey: ["financial-sources", accountId],
    queryFn: () => getFinancialSources(accountId!),
    enabled: !!accountId && (showSourcePicker || showNewSubSourcePicker || !!localSourceId),
    staleTime: 60_000,
  });

  const { data: taskTags = [] } = useQuery<Tag[]>({
    queryKey: ["task-tags", task.id],
    queryFn: () => api.getTaskTags(task.id),
    staleTime: 30_000,
  });

  const addTagMutation = useMutation({
    mutationFn: (tagId: string) => api.addTaskTag(task.id, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["task-tags", task.id] }),
    onError: () => toast({ title: "Failed to add tag", variant: "destructive" }),
  });

  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) => api.removeTaskTag(task.id, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["task-tags", task.id] }),
    onError: () => toast({ title: "Failed to remove tag", variant: "destructive" }),
  });

  const linkSourceMutation = useMutation({
    mutationFn: (sourceId: string | null) =>
      fetchWithAuth<any>(`/api/project-tasks/${task.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ financial_source_id: sourceId }),
      }),
    onSuccess: (_, sourceId) => {
      const matched = sourceId ? accountSources.find((s) => s.id === sourceId) : null;
      setLocalSourceId(sourceId);
      setLocalSource(matched ? { institution_name: matched.institution_name, account_nickname: matched.account_nickname } : null);
      setShowSourcePicker(false);
      queryClient.invalidateQueries({ queryKey: ["project-tasks", task.project_id] });
    },
    onError: () => toast({ title: "Failed to update source link", variant: "destructive" }),
  });

  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    staleTime: 5 * 60 * 1000,
  });

  // Seed rowAssignees from task.users if task_assignees not provided.
  //
  // Do NOT "clean up" the task.users branch as dead code, and do not loosen the condition to
  // task_assignees?.length. This component is shared by two feeders that disagree:
  //   • GET /api/projects/:id/tasks always sets task_assignees (to [] when empty), so on the
  //     project view the first branch always wins — correct, the junction table is authoritative
  //     there.
  //   • taskWithContextToProjectTask (client/src/lib/api.ts) deliberately sets
  //     task_assignees: undefined and fills `users` from assigned_user_id, so on the global
  //     Tasks page and the Account Tasks tab this fallback is the ONLY thing that renders an
  //     assignee.
  // Deleting it blanks assignees on those pages; loosening the condition renders a phantom
  // avatar on the project view for a legacy value the junction table has overridden.
  useEffect(() => {
    if (task.task_assignees) {
      setRowAssignees(task.task_assignees);
    } else if (task.users) {
      setRowAssignees([{
        id: "",
        task_id: task.id,
        user_id: task.users.id,
        list_index: 0,
        users: task.users,
      }]);
    }
  }, [task.id]);


  const toggleStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (toggling) return;
    // Capture position before any awaits — React nullifies currentTarget after first yield
    const btnRect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
    const confettiX = btnRect.left + btnRect.width / 2;
    const confettiY = btnRect.top + btnRect.height / 2;
    // Confirm only when the click does something beyond this one row — an open
    // sub-task cascade, or a client task the client can see. A plain internal task
    // with no sub-tasks completes straight away: a dialog with nothing to say is
    // pure friction on the most common click in the app.
    // Un-completing never confirms (reopening destroys nothing), matching the
    // project checkbox.
    const lines = buildTaskCompleteWarning({
      openSubTaskCount: localSubTasks.filter(s => s.status !== "done" && s.status !== "completed").length,
      isClientTask: task.is_client_task,
      sentAt: task.sent_at,
    });
    if (!done && lines.length > 0) {
      setConfirmComplete({ kind: "task", title: task.title, x: confettiX, y: confettiY, lines });
      return;
    }
    await runToggleStatus(confettiX, confettiY);
  };

  const runToggleStatus = async (confettiX: number, confettiY: number) => {
    setToggling(true);
    try {
      if (done) {
        await api.uncompleteTask(task.id);
        onStatusChange?.(task.id, false);
      } else {
        await api.completeTask(task.id);
        onStatusChange?.(task.id, true);
        triggerConfetti(confettiX, confettiY);
        // Cascade: complete all open subtasks
        const openSubs = localSubTasks.filter(s => s.status !== "done" && s.status !== "completed");
        if (openSubs.length > 0) {
          await Promise.allSettled(openSubs.map((s) => api.completeTask(s.id)));
          setLocalSubTasks(prev => prev.map(s => ({ ...s, status: "done" })));
        }
      }
    } catch {
      toast({ title: "Failed to update task", variant: "destructive" });
    } finally {
      setToggling(false);
    }
  };

  const toggleSubTask = async (e: React.MouseEvent, sub: ProjectTask) => {
    e.stopPropagation();
    if (subTaskToggling[sub.id]) return;
    // Capture position before any awaits — React nullifies currentTarget after first yield
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
    const confettiX = rect.left + rect.width / 2;
    const confettiY = rect.top + rect.height / 2;
    // Only the LAST open sub-task confirms, because only that one completes the
    // parent as well. Ticking off sub-tasks in a run stays a single click each.
    const otherOpen = localSubTasks.filter(
      s => s.id !== sub.id && s.status !== "done" && s.status !== "completed",
    ).length;
    const lines = buildSubTaskCompleteWarning({
      isLastOpenSubTask: otherOpen === 0,
      parentTitle: task.title,
      parentDone: done,
    });
    if (sub.status !== "completed" && sub.status !== "done" && lines.length > 0) {
      setConfirmComplete({ kind: "sub", sub, title: sub.title, x: confettiX, y: confettiY, lines });
      return;
    }
    await runToggleSubTask(sub, confettiX, confettiY);
  };

  const runToggleSubTask = async (sub: ProjectTask, confettiX: number, confettiY: number) => {
    setSubTaskToggling(prev => ({ ...prev, [sub.id]: true }));
    const subDone = sub.status === "completed" || sub.status === "done";
    try {
      if (subDone) {
        await api.uncompleteTask(sub.id);
        setLocalSubTasks(prev => prev.map(s => s.id === sub.id ? { ...s, status: "open" } : s));
      } else {
        await api.completeTask(sub.id);
        const updatedSubs = localSubTasks.map(s => s.id === sub.id ? { ...s, status: "done" } : s);
        setLocalSubTasks(updatedSubs);
        // Auto-complete parent task if all subtasks are now done
        const allSubsDone = updatedSubs.every(s => s.status === "done" || s.status === "completed");
        if (allSubsDone && !done) {
          await api.completeTask(task.id);
          onStatusChange?.(task.id, true);
          triggerConfetti(confettiX, confettiY);
        }
      }
    } catch {
      toast({ title: "Failed to update sub-task", variant: "destructive" });
    } finally {
      setSubTaskToggling(prev => ({ ...prev, [sub.id]: false }));
    }
  };

  const addSubTask = async () => {
    if (!newSubTaskTitle.trim() || !task.project_id) return;
    setAddingSubTask(true);
    try {
      const created = await api.createProjectTask(task.project_id, {
        title: newSubTaskTitle.trim(),
        section_id: task.section_id,
        parent_task_id: task.id,
        financial_source_id: newSubSourceId || null,
      });
      setLocalSubTasks(prev => [...prev, created]);
      setNewSubTaskTitle("");
      setNewSubSourceId(null);
      setNewSubSourceLabel(null);
      setShowAddSubTask(false);
    } catch {
      toast({ title: "Failed to add sub-task", variant: "destructive" });
    } finally {
      setAddingSubTask(false);
    }
  };

  const saveEdit = async () => {
    if (!editTitle.trim()) return;
    setSaving(true);
    try {
      const updates = {
        title: editTitle.trim(),
        due_date: editDueDate || null,
        internal_due_date: editInternalDue || null,
      };
      await api.updateProjectTask(task.id, updates);
      onUpdate?.(task.id, updates);
      setEditing(false);
      toast({ title: "Task updated" });
    } catch {
      toast({ title: "Failed to update task", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const deleteTask = async () => {
    setDeleting(true);
    try {
      await api.deleteProjectTask(task.id);
      onDelete?.(task.id);
      toast({ title: "Task deleted" });
    } catch {
      toast({ title: "Failed to delete task", variant: "destructive" });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const fetchDetail = async () => {
    setLoadingDetail(true);
    try {
      const [cmts, atts, subs] = await Promise.allSettled([
        fetchWithAuth<any[]>(`/api/project-tasks/${task.id}/comments`),
        fetchWithAuth<any[]>(`/api/project-tasks/${task.id}/attachments`),
        fetchWithAuth<ProjectTask[]>(`/api/project-tasks?parent_task_id=${task.id}`),
      ]);
      setComments(cmts.status === "fulfilled" ? cmts.value || [] : []);
      setAttachments(atts.status === "fulfilled" ? atts.value || [] : []);
      if (subs.status === "fulfilled" && Array.isArray(subs.value)) {
        setLocalSubTasks([...subs.value].sort((a, b) => (a.list_index ?? 0) - (b.list_index ?? 0)));
      }
    } catch {
      // ignore
    } finally {
      setFetched(true);
      setLoadingDetail(false);
    }
  };

  // Auto-expand and scroll into view when opened from a notification deep-link
  useEffect(() => {
    if (!autoExpand) return;
    if (!fetched) fetchDetail();
    setTimeout(() => rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpand]);

  // Load users list lazily when comment box opens for @mention autocomplete
  useEffect(() => {
    if (showCommentBox && mentionUsers.length === 0) {
      api.getUsers().then((users) => setMentionUsers(users ?? [])).catch(() => {});
    }
  }, [showCommentBox]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !fetched) fetchDetail();
    if (!next) {
      // Reset transient states on collapse
      setEditing(false);
      setShowAddSubTask(false);
      setNewSubTaskTitle("");
      setConfirmDelete(false);
      setConfirmDeleteAttachmentId(null);
      setShowCommentBox(false);
      setNewComment("");
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const readyAttachmentIds = pendingAttachments.filter((a) => !a.uploading).map((a) => a.id);
      await fetchWithAuth(`/api/project-tasks/${task.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: newComment.trim(), is_client_visible: false, attachment_ids: readyAttachmentIds }),
      });
      setNewComment("");
      setShowCommentBox(false);
      setPendingAttachments([]);
      const cmts = await fetchWithAuth<any[]>(`/api/project-tasks/${task.id}/comments`);
      setComments(cmts || []);
      toast({ title: "Comment added" });
    } catch (err: any) {
      toast({ title: `Comment failed: ${err?.message || "unknown error"}`, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewComment(val);
    const cursor = e.target.selectionStart ?? val.length;
    const match = val.slice(0, cursor).match(/@(\w*)$/);
    setMentionQuery(match ? match[1].toLowerCase() : null);
  };

  const uploadCommentFile = async (file: File) => {
    const tempId = `pending-${Date.now()}`;
    setPendingAttachments((prev) => [...prev, { id: tempId, name: file.name, uploading: true }]);
    try {
      const { uploadUrl, attachmentId } = await fetchWithAuth<{ uploadUrl: string; attachmentId: string }>(
        `/api/project-tasks/${task.id}/comments/upload-url`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fileName: file.name, contentType: file.type || "application/octet-stream" }) }
      );
      await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
      await fetchWithAuth(`/api/task-attachments/${attachmentId}/size`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ file_size: file.size }),
      });
      setPendingAttachments((prev) => prev.map((a) => a.id === tempId ? { id: attachmentId, name: file.name, uploading: false } : a));
    } catch {
      setPendingAttachments((prev) => prev.filter((a) => a.id !== tempId));
      toast({ title: "File upload failed", variant: "destructive" });
    }
  };

  const handleCommentPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageFiles = Array.from(e.clipboardData.items)
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (imageFiles.length === 0) return;
    e.preventDefault();
    imageFiles.forEach(uploadCommentFile);
  };

  const insertMention = (user: User) => {
    const cursor = commentTextareaRef.current?.selectionStart ?? newComment.length;
    const before = newComment.slice(0, cursor);
    const after = newComment.slice(cursor);
    const match = before.match(/@(\w*)$/);
    const newBefore = match
      ? before.slice(0, before.length - match[0].length) + `@${user.name.replace(/\s+/g, "")} `
      : before + `@${user.name.replace(/\s+/g, "")} `;
    setNewComment(newBefore + after);
    setMentionQuery(null);
    setTimeout(() => commentTextareaRef.current?.focus(), 0);
  };

  const downloadAttachment = async (att: any) => {
    try {
      // mode=download: a real save, not the inline URL the preview modal uses (ENG-109).
      const { url } = await fetchWithAuth<{ url: string }>(`/api/project-tasks/${task.id}/attachments/${att.id}/download?mode=download`);
      triggerDownload(url, att.name);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  // ENG-73: staff can remove a file from a task / client task. The delete is permanent
  // (row + stored blob), hence the inline confirm step, mirroring the task delete above.
  const deleteAttachment = async (att: any) => {
    setDeletingAttachmentId(att.id);
    try {
      await fetchWithAuth(`/api/task-attachments/${att.id}`, { method: "DELETE" });
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      // A comment-scoped file shows up in both lists (the task attachments query is not
      // filtered by comment_id), so drop it from the comment copies too.
      setComments((prev) =>
        prev.map((c) => (c.attachments?.some((a: any) => a.id === att.id)
          ? { ...c, attachments: c.attachments.filter((a: any) => a.id !== att.id) }
          : c)),
      );
      setConfirmDeleteAttachmentId(null);
      toast({ title: "Attachment deleted" });
    } catch (err: any) {
      toast({ title: `Delete failed: ${err?.message || "unknown error"}`, variant: "destructive" });
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const isPreviewable = (att: any) => {
    const t = (att.file_type ?? "").toLowerCase();
    const n = (att.name ?? "").toLowerCase();
    return t === "application/pdf" || n.endsWith(".pdf") || t.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|bmp)$/.test(n);
  };

  const openPreview = async (att: any) => {
    try {
      const { url } = await fetchWithAuth<{ url: string }>(`/api/project-tasks/${task.id}/attachments/${att.id}/download`);
      setPreviewName(att.name);
      setPreviewUrl(url);
    } catch {
      toast({ title: "Preview failed", variant: "destructive" });
    }
  };

  const isOverdueTask = task.due_date && !done && task.due_date < new Date().toISOString().slice(0, 10);
  const isBlocked = !done && ((task.is_blocked && task.predecessor_task_id) || !!sequentialBlockedOn);
  const blockedOnTitle = (!done && sequentialBlockedOn) || task.predecessor_title || null;
  const subTasks = localSubTasks;

  return (
    <div ref={rowRef} className="border-b last:border-0 overflow-hidden">
      <div className="flex items-start gap-1.5 py-1.5 hover:bg-muted/30 -mx-1 px-1 rounded group min-w-0">
        {/* Checkbox */}
        <button
          type="button"
          onClick={toggleStatus}
          disabled={toggling}
          className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${done ? "bg-green-500 border-green-500 hover:bg-green-600" : "border-gray-300 hover:border-green-400"}`}
          title={done ? "Mark incomplete" : "Mark complete"}
        >
          {toggling
            ? <Loader2 className="w-2.5 h-2.5 animate-spin text-white" />
            : done && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </button>

        {/* Confirm completing this task or one of its sub-tasks */}
        <CompleteConfirmDialog
          open={!!confirmComplete}
          onOpenChange={(open) => { if (!open) setConfirmComplete(null); }}
          subject={confirmComplete?.kind === "sub" ? "sub-task" : "task"}
          name={confirmComplete?.title}
          lines={confirmComplete?.lines ?? []}
          onConfirm={() => {
            const target = confirmComplete;
            setConfirmComplete(null);
            if (!target) return;
            if (target.kind === "sub") runToggleSubTask(target.sub, target.x, target.y);
            else runToggleStatus(target.x, target.y);
          }}
        />

        {/* Expand/collapse chevron */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
          className="shrink-0 w-5 h-5 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors rounded"
          title={expanded ? "Collapse" : "Expand details"}
        >
          {expanded
            ? <ChevronDown className="w-4 h-4" />
            : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Title area — div so pencil button can live inside */}
        <div
          className="flex-1 flex items-center gap-1.5 min-w-0 cursor-pointer"
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
        >
          <span className={`text-sm min-w-0 line-clamp-2 font-medium ${done ? "line-through text-muted-foreground" : isBlocked ? "text-muted-foreground" : "text-foreground"}`}>
            {task.title}
          </span>
          {/* Portal response indicator */}
          {!!portalResponseCount && portalResponseCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${accountId || task.account_id}?tab=portal`); }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold shrink-0 shadow-sm transition-colors"
              title="View client portal responses"
            >
              📎 {portalResponseCount} {portalResponseCount === 1 ? "RESPONSE" : "RESPONSES"}
            </button>
          )}
          {/* Edit pencil — right after title text */}
          <button
            type="button"
            title="Edit task"
            onClick={(e) => { e.stopPropagation(); setEditing(true); setExpanded(true); if (!fetched) fetchDetail(); }}
            className="shrink-0 p-0.5 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {/* Project chip — visible in collapsed row, links to project */}
          {task.project_title && task.account_id && task.project_id && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${task.account_id}?openProject=${task.project_id}`); }}
              className="shrink-0 text-[10px] text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5 transition-colors max-w-[200px] truncate"
              title={`Go to project: ${task.project_title}`}
            >
              {task.project_title}
            </button>
          )}
          {/* Client chip — only when not already on account page */}
          {!accountId && task.account_name && task.account_id && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${task.account_id}`); }}
              className="shrink-0 text-[10px] text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded px-1.5 py-0.5 transition-colors max-w-[160px] truncate"
              title={`Go to client: ${task.account_name}`}
            >
              {task.account_name}
            </button>
          )}
          {/* Financial source link — right after pencil */}
          {accountId && (
            <div className="shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
              {localSourceId && localSource ? (
                <div className="flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                  <Link2 className="w-2.5 h-2.5 shrink-0" />
                  <span className="max-w-[80px] truncate font-medium">{localSource.account_nickname || localSource.institution_name}</span>
                  <button
                    onClick={() => {
                      const full = accountSources.find(s => s.id === localSourceId);
                      if (full) setEditingSource(full);
                    }}
                    className="ml-0.5 hover:text-blue-600 transition-colors"
                    title="Edit source"
                  >
                    <Pencil className="w-2.5 h-2.5" />
                  </button>
                  <button
                    onClick={() => linkSourceMutation.mutate(null)}
                    disabled={linkSourceMutation.isPending}
                    className="hover:text-rose-600 transition-colors"
                    title="Unlink source"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ) : (
                <Popover open={showSourcePicker} onOpenChange={setShowSourcePicker}>
                  <PopoverTrigger asChild>
                    <button className="p-0.5 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-800 transition-colors" title="Link a financial source">
                      <Link2 className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-2 border-b flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground">Link Financial Source</p>
                      {accountId && (
                        <a href={`/accounts/${accountId}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5" title="Open Sources tab on client profile">
                          Manage <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {accountSources.length === 0 ? (
                        <div className="p-3 space-y-2">
                          <p className="text-xs text-muted-foreground">No sources for this client yet.</p>
                          <button type="button" onClick={() => { setShowSourcePicker(false); setAddSourceFor("task"); }} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add a source
                          </button>
                        </div>
                      ) : (
                        <>
                          {accountSources.map((s) => (
                            <button key={s.id} type="button" onClick={() => { linkSourceMutation.mutate(s.id); setShowSourcePicker(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2">
                              <Link2 className="w-3 h-3 shrink-0 text-emerald-600" />
                              <div className="min-w-0">
                                <div className="font-medium truncate">{s.account_nickname || s.institution_name}</div>
                                {s.account_nickname && <div className="text-[10px] text-muted-foreground truncate">{s.institution_name}</div>}
                              </div>
                            </button>
                          ))}
                          <div className="border-t p-1.5 flex items-center justify-between">
                            <button type="button" onClick={() => { setShowSourcePicker(false); setAddSourceFor("task"); }} className="text-xs text-blue-600 hover:underline flex items-center gap-1 px-1">
                              <Plus className="w-3 h-3" /> Add new source
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
          {/* Spacer pushes badges to right */}
          <span className="flex-1" />
          {isBlocked && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded border border-orange-300 bg-orange-50 text-orange-700 shrink-0 font-medium"
              title={blockedOnTitle ? `Waiting on: ${blockedOnTitle}` : "Blocked by predecessor task"}
            >
              Blocked
            </span>
          )}
          {task.is_client_task && (
            <span className="text-[10px] px-1 py-0.5 rounded border text-muted-foreground shrink-0">Client</span>
          )}
          {allowClientUpload && (
            <Paperclip className="w-3 h-3 text-blue-500 shrink-0" aria-label="Client upload enabled" />
          )}
          {localSubTasks.length > 0 && (
            <span className="text-[10px] shrink-0 px-1.5 py-0.5 rounded-full bg-muted border font-medium text-foreground/70">
              {localSubTasks.filter(s => s.status === "done" || s.status === "completed").length}/{localSubTasks.length}
            </span>
          )}
          {!done ? (
            <>
              <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap" title="Internal due date">
                <span className="opacity-50">Int:</span>{" "}
                {task.internal_due_date
                  ? <span className="font-semibold text-foreground/80">{fmt(task.internal_due_date)}</span>
                  : <span className="opacity-30">—</span>}
              </span>
              <span className={`text-[10px] shrink-0 whitespace-nowrap font-semibold ${isOverdueTask ? "text-red-600" : "text-muted-foreground"}`} title="Due date">
                <span className={`font-normal ${isOverdueTask ? "text-red-400" : "opacity-50"}`}>Due:</span>{" "}
                {task.due_date
                  ? <span>{fmt(task.due_date)}</span>
                  : <span className="opacity-30 font-normal">—</span>}
              </span>
            </>
          ) : (
            task.completed_at && (
              <span className="text-[10px] shrink-0 text-green-600 whitespace-nowrap" title="Completed date">
                ✓ {fmt(task.completed_at)}
              </span>
            )
          )}
          {fetched && comments.length > 0 && (
            <span className="text-[10px] text-muted-foreground shrink-0" title={`${comments.length} comment${comments.length !== 1 ? "s" : ""}`}>
              💬 {comments.length}
            </span>
          )}
          {fetched && attachments.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0" title={`${attachments.length} attachment${attachments.length !== 1 ? "s" : ""}`}>
              <Paperclip className="w-2.5 h-2.5" />{attachments.length}
            </span>
          )}
        </div>

        {/* Tag chips inline on task row */}
        {taskTags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap shrink-0" onClick={(e) => e.stopPropagation()}>
            {taskTags.map((tag: Tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
          </div>
        )}
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <TagPicker
            appliedTags={taskTags}
            onAdd={(tagId) => addTagMutation.mutate(tagId)}
            onRemove={(tagId) => removeTagMutation.mutate(tagId)}
            align="end"
          />
        </div>

        {/* Assignee avatars + popover (FC-style) */}
        <AssigneePopover
          taskId={task.id}
          allUsers={allUsers}
          initialAssignees={rowAssignees}
          onChanged={setRowAssignees}
        />

        {/* Automation rules icon */}
        <button
          type="button"
          title="Task automations"
          onClick={(e) => { e.stopPropagation(); setTaskRulesOpen(true); }}
          className={`shrink-0 p-0.5 rounded transition-colors opacity-90 ${
            taskRuleCount !== null && taskRuleCount > 0
              ? "text-violet-600 hover:text-violet-800 hover:bg-violet-50"
              : "text-muted-foreground/60 hover:text-violet-600 hover:bg-violet-50"
          }`}
        >
          <Zap className="w-4 h-4" />
        </button>

        {/* Delete button */}
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            title="Delete task"
            disabled={deleting}
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
            className="p-0.5 rounded hover:bg-rose-50 text-rose-600 hover:text-rose-800 transition-colors"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
          <DeleteConfirmDialog
            open={confirmDelete}
            onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}
            subject="task"
            name={task.title}
            lines={buildTaskDeleteWarning({
              subTaskCount: localSubTasks.length,
              isClientTask: task.is_client_task,
              sentAt: task.sent_at,
            })}
            busy={deleting}
            onConfirm={deleteTask}
          />
        </div>
      </div>

      {/* Add / Edit Financial Source modal */}
      {accountId && (addSourceFor || editingSource) && (
        <AddFinancialSourceDialog
          accountId={accountId}
          open={!!(addSourceFor || editingSource)}
          onOpenChange={(v) => { if (!v) { setAddSourceFor(null); setEditingSource(null); } }}
          editSource={editingSource}
          onCreated={(created) => {
            if (addSourceFor === "task") {
              linkSourceMutation.mutate(created.id);
            } else {
              const label = created.account_nickname || created.institution_name;
              setNewSubSourceId(created.id);
              setNewSubSourceLabel(label);
              if (!newSubTaskTitle.trim()) setNewSubTaskTitle(label);
            }
            setAddSourceFor(null);
          }}
          onUpdated={(updated) => {
            setLocalSource({ institution_name: updated.institution_name, account_nickname: updated.account_nickname });
            setEditingSource(null);
          }}
        />
      )}

      {/* Task automations modal */}
      <TaskAutomationsModal
        taskId={taskRulesOpen ? task.id : null}
        onClose={() => setTaskRulesOpen(false)}
        onRulesLoaded={setTaskRuleCount}
        accountId={accountId}
      />

      {expanded && (
        <div className="pl-8 pr-1 pb-3 space-y-3">
          {/* Breadcrumb + nav buttons — only when used outside account context */}
          {!accountId && (task.account_name || task.project_title) && (
            <div className="flex items-center justify-between px-2 py-1.5 mb-2 bg-muted/30 rounded text-xs text-muted-foreground">
              <span className="truncate">
                {task.account_name && <span className="font-medium text-foreground">{task.account_name}</span>}
                {task.account_name && task.project_title && <span className="mx-1">—</span>}
                {task.project_title && <span>{task.project_title}</span>}
              </span>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                {task.account_id && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${task.account_id}`); }}
                    className="px-2 py-0.5 rounded border border-border bg-background hover:bg-muted text-xs font-medium transition-colors"
                  >
                    Go to Client
                  </button>
                )}
                {task.account_id && task.project_id && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${task.account_id}?openProject=${task.project_id}`); }}
                    className="px-2 py-0.5 rounded border border-border bg-background hover:bg-muted text-xs font-medium transition-colors"
                  >
                    Go to Project
                  </button>
                )}
              </div>
            </div>
          )}

          {loadingDetail && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading…
            </div>
          )}

          {/* Inline edit form */}
          {editing && (
            <div className="space-y-2 p-2.5 bg-muted/30 rounded border">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Title</label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="h-7 text-xs mt-0.5"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Due Date</label>
                  <Input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    className="h-7 text-xs mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Task Date</label>
                  <Input
                    type="date"
                    value={editInternalDue}
                    onChange={(e) => setEditInternalDue(e.target.value)}
                    className="h-7 text-xs mt-0.5"
                  />
                </div>
              </div>
              <div className="flex items-center gap-1.5 pt-1">
                <Button size="sm" className="h-6 text-xs px-2" onClick={saveEdit} disabled={!editTitle.trim() || saving}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => {
                  setEditing(false);
                  setEditTitle(task.title);
                  setEditDueDate(task.due_date ?? "");
                  setEditInternalDue(task.internal_due_date ?? "");
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}


          {/* Blocked banner — sequential-section wait or legacy predecessor */}
          {isBlocked && blockedOnTitle && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-orange-50 border border-orange-200 dark:bg-orange-950 dark:border-orange-800 text-xs text-orange-800 dark:text-orange-300">
              <span className="font-semibold shrink-0">Blocked</span>
              <span className="text-orange-600 dark:text-orange-400">waiting on:</span>
              <span className="truncate">{blockedOnTitle}</span>
              {!sequentialBlockedOn && task.predecessor_status && (
                <span className="shrink-0 ml-auto text-[10px] text-orange-500">({task.predecessor_status})</span>
              )}
            </div>
          )}

          {/* Financial source quick-reference — shown when a source is linked */}
          {localSourceId && localSource && (() => {
            const fullSource = accountSources.find(s => s.id === localSourceId);
            const name = localSource.account_nickname || localSource.institution_name;
            const sub = localSource.account_nickname ? localSource.institution_name : null;
            return (
              <div className="flex flex-col gap-1 px-2.5 py-2 rounded-md bg-emerald-50 border border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                  <Link2 className="w-3 h-3 shrink-0" />
                  <span>{name}</span>
                  {sub && <span className="font-normal text-emerald-600 dark:text-emerald-400">— {sub}</span>}
                </div>
                {fullSource?.external_hint && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">
                    <span className="font-medium">Hint:</span> {fullSource.external_hint}
                  </p>
                )}
                {fullSource?.login_url && (
                  <a
                    href={fullSource.login_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                  >
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    {fullSource.login_url}
                  </a>
                )}
              </div>
            );
          })()}

          {/* Client upload toggle — only for client tasks */}
          {task.is_client_task && (
            <div className="flex items-center justify-between px-2.5 py-1.5 rounded bg-blue-50 border border-blue-200 dark:bg-blue-950 dark:border-blue-800">
              <div className="flex items-center gap-1.5 text-xs text-blue-800 dark:text-blue-300">
                <Paperclip className="w-3 h-3 shrink-0" />
                <span>Allow client to upload documents</span>
              </div>
              <button
                type="button"
                onClick={async () => {
                  const next = !allowClientUpload;
                  setAllowClientUpload(next);
                  try {
                    await api.updateProjectTask(task.id, { allow_client_upload: next });
                    onUpdate?.(task.id, { allow_client_upload: next });
                  } catch {
                    setAllowClientUpload(!next);
                    toast({ title: "Failed to update task", variant: "destructive" });
                  }
                }}
                className={`relative inline-flex h-4 w-8 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${allowClientUpload ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
              >
                <span className={`pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${allowClientUpload ? "translate-x-4" : "translate-x-0"}`} />
              </button>
            </div>
          )}

          {/* Per-person portal visibility — only for client tasks on accounts with 2+ portal users */}
          {task.is_client_task && (accountId || task.account_id) && (
            <div className="flex items-center justify-between px-2.5 py-1.5 rounded bg-violet-50 border border-violet-200 dark:bg-violet-950 dark:border-violet-800">
              <div className="flex items-center gap-1.5 text-xs text-violet-800 dark:text-violet-300">
                <Eye className="w-3 h-3 shrink-0" />
                <span>Portal visibility</span>
                {!task.sent_at && (
                  <span
                    className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800 font-medium"
                    title="The portal only shows client tasks after they are sent. Use Send Now on the section (or Send to client) to issue it."
                  >
                    Hidden from the portal until sent
                  </span>
                )}
              </div>
              <PortalVisibilityPicker
                accountId={(accountId || task.account_id)!}
                value={visibleToEmails}
                onChange={async (emails) => {
                  const prevValue = visibleToEmails;
                  setVisibleToEmails(emails);
                  try {
                    await api.updateProjectTask(task.id, { visible_to_emails: emails } as any);
                    onUpdate?.(task.id, { visible_to_emails: emails } as any);
                  } catch {
                    setVisibleToEmails(prevValue);
                    toast({ title: "Failed to update visibility", variant: "destructive" });
                  }
                }}
              />
            </div>
          )}

          {/* Sub-tasks — always visible */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Sub-tasks{subTasks.length > 0 ? ` (${subTasks.filter(s => s.status === "done" || s.status === "completed").length}/${subTasks.length})` : ""}
              </p>
              {!showAddSubTask && (
                <button
                  type="button"
                  onClick={() => setShowAddSubTask(true)}
                  className="flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                >
                  <Plus className="w-2.5 h-2.5" />Add
                </button>
              )}
            </div>
            {subTasks.length === 0 && !showAddSubTask && (
              <button
                type="button"
                onClick={() => setShowAddSubTask(true)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded border border-dashed border-muted-foreground/30 text-xs text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors"
              >
                <Plus className="w-3 h-3 shrink-0" />
                Add a sub-task
              </button>
            )}
            <div className="space-y-1">
              {subTasks.map((sub) => {
                const subDone = sub.status === "done" || sub.status === "completed";
                return (
                  <div
                    key={sub.id}
                    draggable
                    onDragStart={(e) => { setDragSubId(sub.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); if (sub.id !== dragSubId) setDragOverSubId(sub.id); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (!dragSubId || dragSubId === sub.id) { setDragSubId(null); setDragOverSubId(null); return; }
                      const currentSubs = [...localSubTasks];
                      const fromIdx = currentSubs.findIndex((s) => s.id === dragSubId);
                      const toIdx = currentSubs.findIndex((s) => s.id === sub.id);
                      setDragSubId(null); setDragOverSubId(null);
                      if (fromIdx === -1 || toIdx === -1) return;
                      const [moved] = currentSubs.splice(fromIdx, 1);
                      currentSubs.splice(toIdx, 0, moved);
                      // Update list_index in local state so subsequent drags compare correctly
                      const reordered = currentSubs.map((s, idx) => ({ ...s, list_index: idx }));
                      setLocalSubTasks(reordered);
                      // Persist all positions — errors surface rather than being silently swallowed
                      Promise.all(
                        reordered.map((s) =>
                          fetchWithAuth(`/api/project-tasks/${s.id}`, {
                            method: "PUT", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ list_index: s.list_index }),
                          })
                        )
                      ).catch(() => toast({ title: "Failed to save task order", variant: "destructive" }));
                    }}
                    onDragEnd={() => { setDragSubId(null); setDragOverSubId(null); }}
                    className={`flex items-center gap-0.5 rounded ${dragOverSubId === sub.id ? "border-t-2 border-primary" : ""} ${dragSubId === sub.id ? "opacity-40" : ""}`}
                  >
                    <span className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground">
                      <GripVertical className="w-3 h-3" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <SubTaskRow
                        sub={sub}
                        subDone={subDone}
                        toggling={!!subTaskToggling[sub.id]}
                        onToggle={(e) => toggleSubTask(e, sub)}
                        onUpdate={(updated) => setLocalSubTasks(prev => prev.map(s => s.id === updated.id ? updated : s))}
                        onDelete={(subId) => setLocalSubTasks(prev => prev.filter(s => s.id !== subId))}
                        accountId={accountId}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {showAddSubTask && (
              <div className="space-y-1.5 mt-1.5">
                <div className="flex items-center gap-1.5">
                  {/* Source picker LEFT of input */}
                  {accountId && (
                    <Popover open={showNewSubSourcePicker} onOpenChange={setShowNewSubSourcePicker}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={`shrink-0 p-0.5 rounded transition-colors ${newSubSourceId ? "text-emerald-700 bg-emerald-100" : "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"}`}
                          title={newSubSourceId ? `Source: ${newSubSourceLabel}` : "Pick or add a financial source"}
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-0" align="start">
                        <div className="p-2 border-b">
                          <p className="text-xs font-semibold text-muted-foreground">Pick a source (auto-fills title)</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {accountSources.length === 0 ? (
                            <div className="p-3 space-y-2">
                              <p className="text-xs text-muted-foreground">No sources for this client yet.</p>
                              <button type="button" onClick={() => { setShowNewSubSourcePicker(false); setAddSourceFor("subtask"); }} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Add a source
                              </button>
                            </div>
                          ) : (
                            <>
                              {newSubSourceId && (
                                <button type="button" onClick={() => { setNewSubSourceId(null); setNewSubSourceLabel(null); setShowNewSubSourcePicker(false); }} className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted border-b">
                                  ✕ Clear source
                                </button>
                              )}
                              {accountSources.map((s) => (
                                <button key={s.id} type="button" onClick={() => {
                                  const label = s.account_nickname || s.institution_name;
                                  setNewSubSourceId(s.id);
                                  setNewSubSourceLabel(label);
                                  if (!newSubTaskTitle.trim()) setNewSubTaskTitle(label);
                                  setShowNewSubSourcePicker(false);
                                }} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2 ${newSubSourceId === s.id ? "bg-emerald-50 text-emerald-700" : ""}`}>
                                  <Link2 className="w-3 h-3 shrink-0 text-emerald-600" />
                                  <div className="min-w-0">
                                    <div className="font-medium truncate">{s.account_nickname || s.institution_name}</div>
                                    {s.account_nickname && <div className="text-[10px] text-muted-foreground truncate">{s.institution_name}</div>}
                                  </div>
                                  {newSubSourceId === s.id && <Check className="w-3 h-3 ml-auto shrink-0 text-emerald-600" />}
                                </button>
                              ))}
                              <div className="border-t p-1.5">
                                <button type="button" onClick={() => { setShowNewSubSourcePicker(false); setAddSourceFor("subtask"); }} className="text-xs text-blue-600 hover:underline flex items-center gap-1 px-1">
                                  <Plus className="w-3 h-3" /> Add new source
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  <Input
                    autoFocus
                    placeholder={newSubSourceLabel ? newSubSourceLabel : "Sub-task title…"}
                    value={newSubTaskTitle}
                    onChange={(e) => setNewSubTaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addSubTask();
                      if (e.key === "Escape") { setShowAddSubTask(false); setNewSubTaskTitle(""); setNewSubSourceId(null); setNewSubSourceLabel(null); }
                    }}
                    className="h-6 text-xs flex-1"
                  />
                </div>
                {newSubSourceId && (
                  <div className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 w-fit">
                    <Link2 className="w-2.5 h-2.5" />
                    {newSubSourceLabel}
                    <button type="button" onClick={() => { setNewSubSourceId(null); setNewSubSourceLabel(null); }} className="ml-0.5 hover:text-red-500">×</button>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Button size="sm" className="h-6 px-2 text-xs" onClick={addSubTask} disabled={!newSubTaskTitle.trim() || addingSubTask}>
                    {addingSubTask ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { setShowAddSubTask(false); setNewSubTaskTitle(""); setNewSubSourceId(null); setNewSubSourceLabel(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          {fetched && attachments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Attachments</p>
                {attachments.some((a) => a.uploaded_by_user_id) && firmVisibilityBadge}
              </div>
              <div className="space-y-1">
                {attachments.map((att) => (
                  <div key={att.id} className="text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{att.name}</span>
                      {!att.uploaded_by_user_id && (
                        <span className="text-[10px] px-1 py-0.5 rounded border text-muted-foreground shrink-0">Client</span>
                      )}
                      {att.file_size && (
                        <span className="text-muted-foreground shrink-0">{(att.file_size / 1024).toFixed(0)} KB</span>
                      )}
                      {confirmDeleteAttachmentId !== att.id && (
                        <button
                          type="button"
                          title="Delete attachment"
                          onClick={() => setConfirmDeleteAttachmentId(att.id)}
                          className="shrink-0 p-0.5 rounded text-rose-600 hover:text-rose-800 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="ml-5 flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-primary hover:underline text-xs font-medium shrink-0"
                        onClick={() => downloadAttachment(att)}
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                      {isPreviewable(att) && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium shrink-0"
                          onClick={() => openPreview(att)}
                        >
                          <Eye className="w-3 h-3" />
                          Preview
                        </button>
                      )}
                      {/* Client download receipt (migration 108). Firm-shared files only, and
                          only once the task is actually sent — "Not yet viewed by client" on an
                          internal or unsent task would be meaningless, since the client cannot
                          see the file at all in those states. Staff downloads and staff Portal
                          Preview never stamp it, so a count here always means a real client. */}
                      {att.uploaded_by_user_id && firmVisibility === "portal-visible" && (
                        att.download_count > 0 ? (
                          <span className="text-[10px] text-emerald-700 font-medium truncate min-w-0" title="Client download receipt">
                            ✓ Client downloaded ×{att.download_count}
                            {att.last_downloaded_by && ` — ${att.last_downloaded_by}`}
                            {att.last_downloaded_at && `, ${format(parseISO(att.last_downloaded_at), "MMM d, h:mm a")}`}
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-600 shrink-0" title="Client has not opened or downloaded this file yet">
                            Not yet viewed by client
                          </span>
                        )
                      )}
                      {confirmDeleteAttachmentId === att.id && (
                        <span className="flex items-center gap-1 text-[10px]">
                          <span className="text-muted-foreground">Delete permanently?</span>
                          <button
                            type="button"
                            onClick={() => deleteAttachment(att)}
                            disabled={deletingAttachmentId === att.id}
                            className="px-1 py-0.5 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 text-[10px]"
                          >
                            {deletingAttachmentId === att.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : "Delete"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteAttachmentId(null)}
                            disabled={deletingAttachmentId === att.id}
                            className="px-1 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80 text-[10px]"
                          >
                            No
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {previewUrl && (
            <Dialog open={true} onOpenChange={() => { setPreviewUrl(null); setPreviewName(""); }}>
              <DialogContent className="max-w-4xl w-full" style={{ height: "88vh", display: "flex", flexDirection: "column" }}>
                <DialogHeader>
                  <DialogTitle className="truncate text-sm">{previewName}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-hidden rounded">
                  {/\.(jpe?g|png|gif|webp|heic|bmp)$/i.test(previewName) ? (
                    <div className="w-full h-full flex items-center justify-center bg-muted/30 overflow-auto">
                      <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <iframe src={previewUrl} title={previewName} className="w-full h-full border-0" />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Comments */}
          {fetched && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Comments{comments.length > 0 ? ` (${comments.length})` : ""}
                </p>
                {!showCommentBox && (
                  <button
                    type="button"
                    onClick={() => setShowCommentBox(true)}
                    className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                  >
                    <Plus className="w-2.5 h-2.5" />Add comment
                  </button>
                )}
              </div>
              {comments.length === 0 && !showCommentBox ? (
                <p className="text-xs text-muted-foreground italic">No comments yet</p>
              ) : (
                <div className="space-y-2">
                  {comments.map((c) => (
                    <div key={c.id} className="bg-muted/40 rounded px-2.5 py-1.5 text-xs space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{c.users?.name || "Client"}</span>
                        <span className="text-muted-foreground">{fmt(c.created_at)}</span>
                        <LikeButton
                          likedByMe={c.likedByMe}
                          likeCount={c.likeCount}
                          likers={c.likers}
                          onClick={async () => {
                            try {
                              const res = await fetchWithAuth<{ liked: boolean; count: number; likers: string[] }>(`/api/task-comments/${c.id}/like`, { method: "POST" });
                              setComments(prev => prev.map(x => x.id === c.id ? { ...x, likedByMe: res.liked, likeCount: res.count, likers: res.likers } : x));
                            } catch { toast({ title: "Failed to like comment", variant: "destructive" }); }
                          }}
                          className="ml-auto"
                        />
                      </div>
                      <p className="whitespace-pre-wrap">{c.body}</p>
                      {c.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {c.attachments.map((a: any) => {
                            const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(a.name) || (a.file_type && a.file_type.startsWith("image/"));
                            return (
                              <button
                                key={a.id}
                                type="button"
                                className="inline-flex items-center gap-1 text-[10px] bg-background border rounded px-1.5 py-0.5 hover:bg-muted transition-colors"
                                onClick={async () => {
                                  try {
                                    // Images open in the lightbox (inline URL); everything else saves.
                                    const { url } = await fetchWithAuth<{ url: string }>(
                                      `/api/task-attachments/${a.id}/download${isImg ? "" : "?mode=download"}`,
                                    );
                                    if (isImg) setLightboxUrl(url);
                                    else triggerDownload(url, a.name);
                                  } catch { toast({ title: "Download failed", variant: "destructive" }); }
                                }}
                              >
                                <Paperclip className="w-2.5 h-2.5 shrink-0" />
                                <span className="max-w-[140px] truncate">{a.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Comment compose — only shows after clicking Add */}
              {showCommentBox && (
                <div className="space-y-1.5 pt-1.5 mt-1.5 border-t">
                  <div className="relative">
                    <Textarea
                      ref={commentTextareaRef}
                      autoFocus
                      placeholder="Add a comment… (use @Name to notify someone, paste or drop images)"
                      value={newComment}
                      onChange={handleCommentChange}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setMentionQuery(null);
                      }}
                      onPaste={handleCommentPaste}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        Array.from(e.dataTransfer.files).forEach(uploadCommentFile);
                      }}
                      rows={2}
                      className="text-xs resize-none"
                    />
                    {/* @mention dropdown */}
                    {mentionQuery !== null && (() => {
                      const filtered = mentionUsers.filter((u) =>
                        u.name && u.name.replace(/\s+/g, "").toLowerCase().startsWith(mentionQuery!)
                      ).slice(0, 6);
                      if (filtered.length === 0) return null;
                      return (
                        <ul className="absolute z-50 bottom-full mb-1 left-0 w-56 bg-popover border rounded-md shadow-md text-xs overflow-hidden">
                          {filtered.map((u) => (
                            <li key={u.id}>
                              <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                                className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2"
                              >
                                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                                  {(u.name?.[0] ?? "?").toUpperCase()}
                                </span>
                                {u.name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                  {/* Pending attachment chips */}
                  {pendingAttachments.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {firmVisibilityBadge}
                      {pendingAttachments.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-1 text-[10px] bg-muted border rounded px-1.5 py-0.5">
                          {a.uploading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Paperclip className="w-2.5 h-2.5" />}
                          <span className="max-w-[120px] truncate">{a.name}</span>
                          {!a.uploading && (
                            <button type="button" onClick={() => setPendingAttachments((p) => p.filter((x) => x.id !== a.id))} className="text-muted-foreground hover:text-destructive">×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1">
                      <input
                        ref={commentFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCommentFile(f); e.target.value = ""; }}
                      />
                      <button
                        type="button"
                        onClick={() => commentFileInputRef.current?.click()}
                        title="Attach file"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs px-2"
                        onClick={() => { setShowCommentBox(false); setNewComment(""); setMentionQuery(null); setPendingAttachments([]); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={submitComment}
                        disabled={!newComment.trim() || submitting || pendingAttachments.some((a) => a.uploading)}
                      >
                        {submitting
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Send className="w-3 h-3 mr-1" />Send</>}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Image lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
          onKeyDown={(e) => e.key === "Escape" && setLightboxUrl(null)}
          tabIndex={-1}
        >
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-[90vw] max-h-[90vh] rounded shadow-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
